import { FetchState } from 'src/reducers/fetchReducer';
import { AppParams, GetApplicationParams, getApplicationParams } from 'src/api/applicationParams';
import { objectFromEntries } from 'src/utils/objectFromEntries';
import VERTICALS from 'src/enums/Verticals';
import useFetchReducer from 'src/hooks/useFetchReducer';

const getParamValue =
  <K extends AppParams.Name, V extends AppParams.Value>(params: AppParams.Item<K, V>[]) =>
  (name: K, defaultValue: V): V => {
    const found = params.find((el) => el.name === name);
    return found?.value ?? defaultValue;
  };

type UseApplicationParams<K extends AppParams.Name, V extends AppParams.Value> = {
  appId: string;
  appParams: readonly K[];
  extraParams?: Record<string, string>;
  defaultValue: V;
  customFetcher?: (fetchData: GetApplicationParams) => Promise<AppParams.Item<K, V>[]>;
};

export const useApplicationParams = <K extends AppParams.Name, V extends AppParams.Value>({
  appId,
  appParams,
  extraParams,
  defaultValue,
  customFetcher,
}: UseApplicationParams<K, V>): FetchState<Record<K, V>> => {
  const [{ data, loading, error }] = useFetchReducer(() => {
    if (customFetcher) {
      return customFetcher(getApplicationParams);
    }
    return getApplicationParams<K, V>(appId, appParams, extraParams);
  }, [appId]);

  if (data === null) {
    return { data, loading, error };
  }

  const getValue = getParamValue(data);
  return {
    data: objectFromEntries(appParams.map((name) => [name, getValue(name, defaultValue)])),
    loading,
    error,
  };
};

export const useCustomApplicationParams = (appId: string) => {
  const appParams = ['allow_all_autoparts_script_types', 'enable_YMME_searching', 'enable_search_by_category'] as const;
  const { data, loading } = useApplicationParams({
    appId,
    appParams,
    defaultValue: false as boolean,
    customFetcher: async (fetchData) => {
      if (appId === VERTICALS.AUTOPARTSRA) {
        return fetchData(appId, appParams);
      }
      return [
        { name: 'allow_all_autoparts_script_types', value: true },
        { name: 'enable_YMME_searching', value: false },
        { name: 'enable_search_by_category', value: false },
      ];
    },
  });
  const isAutopartsScript = data?.['allow_all_autoparts_script_types'] ?? false;
  const enableYMME = data?.['enable_YMME_searching'] ?? false;
  const enableSBC = data?.['enable_search_by_category'] ?? false;
  return [loading, { isAutopartsScript, enableYMME, enableSBC }] as const;
};

export const useApplicationProductFinderParams = (appId: string) => {
  const { data } = useApplicationParams({
    appId,
    appParams: ['read_only_benchmark', 'read_only_match'],
    defaultValue: false as boolean,
  });
  const isReadOnlyBenchmark = data?.['read_only_benchmark'] ?? false;
  const isReadOnlyMatch = data?.['read_only_match'] ?? false;
  return [{ isReadOnlyBenchmark, isReadOnlyMatch }] as const;
};

// export const useApplicationAnonParams = (appId: string) => {
//   const { data } = useApplicationParams({
//     appId,
//     appParams: [
//       'priv_sys_anon',
//     ],
//     defaultValue: false as boolean,
//   });
//   const isReadOnlyAnonymity = data?.['priv_sys_anon'] ?? null;
//   return [
//     { isReadOnlyAnonymity},
//   ] as const;
// };

export const useApplicationDataCleansingParams = (appId: string) => {
  const { data } = useApplicationParams({
    appId,
    appParams: [
      'anon_admin',
      'ra_data_cleanup',
      'ra_product_cleanup',
      'ra_shallow_cleanup',
      'ra_spider_cleanup',
      'ra_client_management',
      'ra_match_attribute_management',
      'ra_site_management',
      'error_monitoring',
      'ra_data_editor',
      'ra_diagnostic',
      'streaming_status',
      'system_status',
    ],
    defaultValue: true as boolean,
  });
  const isRaDataCleanup = data?.['ra_data_cleanup'] ?? null;
  const isAnonAdmin = data?.['anon_admin'] ?? null;
  const isRaProductCleanup = data?.['ra_product_cleanup'] ?? null;
  const isRaShallowCleanup = data?.['ra_shallow_cleanup'] ?? null;
  const isRaSpiderCleanup = data?.['ra_spider_cleanup'] ?? null;
  const isRaClientMng = data?.['ra_client_management'] ?? null;
  const isRaMatchAttMng = data?.['ra_match_attribute_management'] ?? null;
  const isRaSiteMng = data?.['ra_site_management'] ?? null;
  const isEM = data?.['error_monitoring'] ?? null;
  const isRaDataEditor = data?.['ra_data_editor'] ?? null;
  const isRaDiagnostic = data?.['ra_diagnostic'] ?? null;
  const isStreamingSys = data?.['streaming_status'] ?? null;
  const isSysStatus = data?.['system_status'] ?? null;
  return [
    {
      isRaDataCleanup,
      isRaProductCleanup,
      isRaShallowCleanup,
      isRaSpiderCleanup,
      isRaClientMng,
      isRaMatchAttMng,
      isRaSiteMng,
      isRaDataEditor,
      isRaDiagnostic,
      isEM,
      isStreamingSys,
      isSysStatus,
      isAnonAdmin,
    },
  ] as const;
};